import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SortingState, VisibilityState } from '@tanstack/react-table';

import { useWarehousesQuery } from '@/graphql/queries/warehouses.generated';
import { PickingJobStatus } from '@/graphql/generatedTypes';
import type { ActiveFilter } from '@/views/Order/components/table/OrderToolbar';
import {
  readStorage,
  usePersistedState,
  writeStorage,
} from '@/hooks/usePersistedState';
import i18n from '@/utils/i18n';
import {
  ALL_STATUSES,
  COLUMN_IDS,
  FILTER_UNSET,
  applyClientFilters,
  deliveredFloorApplies,
  filterValue,
  getPickingJobFilterDefinitions,
  isDefaultStatusSelection,
  normalizeFilterValue,
  pickingJobQueries,
  pickupDateArgs,
  readFacetLabels,
  rememberRowFacetLabels,
  selectedFacetLabels,
  splitValues,
  type RowFacetLabels,
} from './pickingJobFilters';
import { compareRows, readActiveFilters, readSorting } from './pickingJobRows';
import type {
  PickingJobFilterDefinition,
  PickingJobRow,
  WarehouseOption,
} from './types';
import { usePickingJobFanOut } from './usePickingJobFanOut';

export const PAGE_SIZE = 20;

// A search that widens past the default status filter. PICKING_NOT_STARTED is
// left out: the backend drops it from a search anyway (it holds no picked
// items), and asking for it would only raise the "not searched" hint for a
// status the user never picked.
const WIDENED_SEARCH_STATUSES = ALL_STATUSES.filter(
  (status) => status !== PickingJobStatus.PickingNotStarted,
);

/** Mirrors colugo's MIN_SEARCH_LENGTH for picked_item_search. */
export const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

// Remembered per browser, like the order list — not per user.
const COLUMN_VISIBILITY_KEY = 'picking-job-list-col-vis';
const FILTERS_KEY = 'picking-job-list-filters';
const FACET_LABELS_KEY = 'picking-job-list-facet-labels';
const SORTING_KEY = 'picking-job-list-sorting';

/**
 * Backs the picking job overview. Warehouse, status and pickup date are query
 * arguments (see usePickingJobFanOut); every other column filters, sorts and
 * pages over the merged set here.
 */
export function usePickingJobList() {
  const {
    data: warehousesData,
    loading: warehousesLoading,
    error: warehousesError,
  } = useWarehousesQuery();

  const warehouses: WarehouseOption[] = useMemo(() => {
    // Two stores can share one item location; the fan-out is per location.
    const byLocation = new Map<string, WarehouseOption>();
    (warehousesData?.warehouses ?? []).forEach((w) => {
      if (w.item_location_id && !byLocation.has(w.item_location_id)) {
        byLocation.set(w.item_location_id, {
          itemLocationId: w.item_location_id,
          name: w.name ?? w.text_id,
        });
      }
    });
    return [...byLocation.values()];
  }, [warehousesData]);

  // Filters, sort and column visibility persist; the page does not.
  const [activeFilters, setActiveFilters] = usePersistedState<ActiveFilter[]>(
    FILTERS_KEY,
    readActiveFilters,
  );
  const [sorting, setSorting] = usePersistedState<SortingState>(
    SORTING_KEY,
    readSorting,
  );
  const [currentPage, setCurrentPage] = useState(1);
  // Two states on purpose: `search` follows the keystrokes so the field stays
  // responsive, `appliedSearch` is what the query runs on. Without the delay
  // every letter of a container name would be its own fan-out across warehouses.
  // Not persisted: a search is a one-off lookup, not a view to come back to.
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [columnVisibility, setColumnVisibility] =
    usePersistedState<VisibilityState>(
      COLUMN_VISIBILITY_KEY,
      (stored) => (stored as VisibilityState | null) ?? {},
    );

  const handleFilterChange = useCallback(
    (id: string, rawValue: string) => {
      const value = normalizeFilterValue(id, rawValue);
      setActiveFilters((prev) =>
        prev.some((f) => f.id === id)
          ? prev.map((f) => (f.id === id ? { ...f, value } : f))
          : [...prev, { id, value }],
      );
      setCurrentPage(1);
    },
    [setActiveFilters],
  );

  const handleFilterAdd = useCallback(
    (id: string) => {
      setActiveFilters((prev) =>
        prev.some((f) => f.id === id)
          ? prev
          : [...prev, { id, value: FILTER_UNSET }],
      );
    },
    [setActiveFilters],
  );

  // Applied only from MIN_SEARCH_LENGTH on: the backend refuses shorter terms
  // (a two-letter leading-wildcard ILIKE would scan every picked row), and
  // one or two characters are never a box anyway. A cleared field applies at
  // once: there is nothing to wait for, and the full list should come back.
  useEffect(() => {
    const trimmed = search.trim();
    const next = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
    // Back to page 1 only when the applied term changes — a trailing space
    // does not change what is listed.
    if (next === appliedSearch) return;
    const apply = () => {
      setAppliedSearch(next);
      setCurrentPage(1);
    };
    const timer = setTimeout(apply, search === '' ? 0 : SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, appliedSearch]);

  const handleFilterRemove = useCallback(
    (id: string) => {
      setActiveFilters((prev) => prev.filter((f) => f.id !== id));
      setCurrentPage(1);
    },
    [setActiveFilters],
  );

  // No warehouse selected means every warehouse.
  const warehouseFilterKey = splitValues(
    filterValue(activeFilters, COLUMN_IDS.warehouse),
  ).join(',');

  const selectedWarehouses = useMemo(() => {
    const ids = warehouseFilterKey ? warehouseFilterKey.split(',') : [];
    return ids.length === 0
      ? warehouses
      : warehouses.filter((w) => ids.includes(w.itemLocationId));
  }, [warehouses, warehouseFilterKey]);

  // "Where is this box?" has to be able to answer from anywhere, so a search
  // run against the untouched default status filter widens to every status.
  // The default is a worklist convenience — the open statuses ops works
  // through — and silently hiding the container someone is hunting for behind
  // it makes the search look broken rather than filtered.
  //
  // Only the default selection widens: a status the user picked themselves is
  // a narrowing they meant, search or no search. The status filter is never
  // empty — both write paths store a cleared one as DEFAULT_STATUSES — but the
  // facet stores values in click order, so the default is recognised as a set,
  // not by its spelling. The widened query is bounded by the search term, not
  // by the 30-day DELIVERED window.
  const statusFilterValue = filterValue(activeFilters, COLUMN_IDS.status);
  const searchIgnoresStatus =
    appliedSearch !== '' && isDefaultStatusSelection(statusFilterValue);

  const selectedStatuses = useMemo(
    () =>
      searchIgnoresStatus
        ? WIDENED_SEARCH_STATUSES
        : (splitValues(statusFilterValue) as PickingJobStatus[]),
    [statusFilterValue, searchIgnoresStatus],
  );

  // The backend drops PICKING_NOT_STARTED from a search: a job that has not
  // been started holds no picked items to match.
  const searchSkipsNotStarted =
    appliedSearch !== '' &&
    selectedStatuses.includes(PickingJobStatus.PickingNotStarted);

  const queries = searchIgnoresStatus
    ? [{ statuses: selectedStatuses, ...pickupDateArgs(activeFilters) }]
    : pickingJobQueries(activeFilters, selectedStatuses);
  const deliveredFloorActive =
    !searchIgnoresStatus &&
    deliveredFloorApplies(activeFilters, selectedStatuses);

  // A destination or job picked from the facet can vanish from the rows on
  // the next load. It stays selected — dropping it would widen the table
  // behind the user's back — and stays in the facet so it can be seen and
  // cleared, under the last label any load showed for it. The labels of the
  // selected values persist with the filters, so a reload keeps them readable.
  const [knownLabels, setKnownLabels] = useState<RowFacetLabels>(() =>
    readFacetLabels(readStorage(FACET_LABELS_KEY)),
  );
  useEffect(() => {
    writeStorage(
      FACET_LABELS_KEY,
      selectedFacetLabels(knownLabels, activeFilters),
    );
  }, [knownLabels, activeFilters]);
  const handleLoad = useCallback((rows: PickingJobRow[]) => {
    setKnownLabels((prev) => rememberRowFacetLabels(prev, rows));
  }, []);

  const {
    rows: allRows,
    failedWarehouses,
    loading,
    error,
  } = usePickingJobFanOut(
    selectedWarehouses,
    queries,
    appliedSearch,
    handleLoad,
  );

  const filteredRows = useMemo(
    () => applyClientFilters(allRows, activeFilters),
    [allRows, activeFilters],
  );

  const sortedRows = useMemo(() => {
    const sort = sorting[0];
    if (!sort) return filteredRows;
    const copy = [...filteredRows];
    copy.sort((a, b) => compareRows(a, b, sort.id, sort.desc));
    return copy;
  }, [filteredRows, sorting]);

  const pageRows = useMemo(
    () =>
      sortedRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedRows, currentPage],
  );

  // Options for the value-driven filters come from what the query returned, so
  // they are built from the server-filtered rows, not the client-filtered ones.
  // While the search is widened the status chip still shows the default it
  // does not apply; it is muted so the result set does not seem to contradict
  // it.
  const availableFilters: PickingJobFilterDefinition[] = useMemo(
    () =>
      getPickingJobFilterDefinitions(
        warehouses,
        allRows,
        activeFilters,
        knownLabels,
      ).map((def) =>
        searchIgnoresStatus && def.id === COLUMN_IDS.status
          ? {
              ...def,
              inactiveNote: i18n.t(
                'Picking:statusFilterInactiveWhileSearching',
              ),
            }
          : def,
      ),
    [warehouses, allRows, activeFilters, knownLabels, searchIgnoresStatus],
  );

  return {
    rows: pageRows,
    totalCount: filteredRows.length,
    availableFilters,
    activeFilters,
    handleFilterChange,
    handleFilterAdd,
    handleFilterRemove,
    search,
    setSearch,
    searchIgnoresStatus,
    sorting,
    setSorting,
    currentPage,
    setCurrentPage,
    columnVisibility,
    setColumnVisibility,
    loading: loading || warehousesLoading,
    error: error ?? warehousesError ?? null,
    failedWarehouses,
    deliveredFloorActive,
    searchSkipsNotStarted,
  };
}
